<template>
	<tool-dialog
		v-bind="$props"
		:searchable="true"
		:expandable="true"
		:filterable="true"
		:filter-items="tags"
		:display-name="$t('elementmanager.title')"
		max-height="20rem"
		@filter-change="updateFilters"
		@show-hide-all="showHideAllTags"
		v-on="$listeners"
	>
		<ul class="element-list" :class="`element-list ${!editMode ? 'edit-class' : ''}`">
			<tree-item
				v-for="(entity, index) in entities"
				:key="entity.id"
				:item="entity"
				:index="index"
				:checked="true"
				@click.native="setFocusedEntity(entity)"
				@change="setEntityVisibility"
				@dblclick="onDblclick"
				@buttonclick="onZoom"
				@pencilclick="onEdit"
				@deleteclick="onDelete"
			/>
		</ul>
		<template #footer>
			<b-button-group v-if="!osmSelect" class="w-100" style="background: gray">				
				<b-button v-b-modal.entity-upload variant="primary" class="mr-1">
					Import
				</b-button>
				<b-modal
					id="entity-upload"
					ref="entity-upload"
					:title="$t('file-manager.add-new-file')"
					:ok-title="$t('file-manager.continue')"
					@ok="uploadSelectedFile()"
					@hidden="uploadFileName = $t('file-manager.no-file-selected')"
				>
					<div class="file-upload">
						<i class="tan-icon-arrow-up" />
						<div v-for="_file in filesInView" :key="_file.name" class="mb-2">
							<li style="cursor:pointer" @click="readFile(_file)">
								{{ _file.name }}
							</li>
						</div>
						<button class="browse-files" @click="clickInput">
							{{ $t('file-manager.browse-files') }}
						</button>
						<div class="selected-file">
							{{ uploadFileName }}
						</div>
						<input
							ref="fileInput"
							type="file"
							hidden
							@change="prepareFileUpload($event)"
						>
					</div>
				</b-modal>
				<b-button variant="primary" @click="toggleOsmSelectionMode(true)">
					Select Buildings
				</b-button>
			</b-button-group>
			<b-button-group v-else class="w-100">
				<b-button variant="danger" @click="toggleOsmSelectionMode(false)">
					Cancel
				</b-button>
				<b-button :disabled="!buildingsSelected" variant="primary" @click="addAllSelectedOsmBuildings">
					Add Selected
				</b-button>
			</b-button-group>
			<div v-show="editMode" class="edit-tool">
				<div style="width: 100%;">
					<header class="d-flex bg-primary">
						<span class="w-100 text-white pl-2 text-bold name">
							{{
								focusedEntityFilterProperty
									? `${focusedName}: ${focusedEntityFilterProperty}`
									: `Appearance Editor: ${focusedName} ${focusedEntityFilterProperty}`
							}}</span>

						<button
							class="btn btn-sm btn-secondary text-primary px-2 shadow-none"
							@click="editMode = false"
						>
							<i class="tan-icon-close-window" />
						</button>
					</header>
					<div class="w-100 text-center">
						<color-picker style="width:100%;" :value="colors" @input="setEntityColor" />
					</div>
					<b-button-group class="w-100">
						<b-button variant="danger" @click="editMode = false">
							Exit
						</b-button>
						<b-button variant="primary" @click="editMode = false">
							Save
						</b-button>
					</b-button-group>
				</div>
			</div>
		</template>
	</tool-dialog>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { Chrome } from 'vue-color';
import BaseTool from '@/components/Project/Tool/BaseTool.vue';
import TreeItem from '@/components/TreeItem.vue';

const { flatten } = require('flat');
const _debounce = require('lodash/debounce');
const _set = require('lodash/set');

export default {
	name: 'ElementManager',
	components: {
		TreeItem,
		'color-picker': Chrome,
	},
	extends: BaseTool,
	data() {
		return {
			query: '',
			queryBuffer: '',
			tagPaneIsOpen: false,
			osmSelectionMode: false,
			brightness: 0.3,
			editMode: false,
			focusedName: '',
			focusedEntityId: '',
			focusedEntityFilter: null,
			focusedEntityFilterProperty: null,
			colors: {
				rgba: { r: 213, g: 213, b: 255, a: 1 },
			},
			entities: [{ name: 'Loading' }],
			filesInView: [],
			file: '',
			uploadFileName: this.$t('file-manager.no-file-selected'),
		};
	},
	computed: {
		...mapState([
			'openPanels',
			'tags',
			'osmSelect',
			'buildingsSelected',
		]),

		osmSelectActive() {
			return this.$czm('osmSelectActive');
		},
	},
	watch: {
		queryBuffer: _debounce((newVal) => {
			this.query = newVal;
		}, 500),
		scenario(newVal) {
			if (newVal) this.$czm('getTreeMenu');
		},
	},

	created() {
		this.$root.$off('menu-message');
		this.$root.$off('patch-menu');
		this.$root.$off('selected-building');
		this.$root.$on('menu-message', (msg) => {
			this.entities = msg;
			this.entities
				.filter((entity) => entity.data[0].data.filterProperty)
				.map((x) => [
					x.id,
					x.data[0].data.filterProperty,
					x.data[0].data.propertyFilters[
					x.data[0].data.filterProperty
					],
				])
				.forEach((x) => {
					this.entities.find(
						(y) => y.id === x[0]
					).children = x[2].map((z, i) => {
						return {
							id: `${x[0]}-${z}`,
							parent: x[0],
							name: `${z}`,
							filter: x[1],
							tags: ['filterProperty', x[1]],
						};
					});
				});
			return true;
		});
		this.$root.$on('patch-menu', async (msg) => {
			this.setProperty(msg.id, 'name', msg.name);
		});
		this.$root.$on('selected-building', async (msg) => {
			const { name, elementId, ...args } = msg;
			const children = Object.entries(args).map((x, i) => {
				return {
					id: `${elementId}-${i}`,
					parent: elementId,
					name: `${x[0]}: ${x[1]}`,
					temp: 'true',
					tags: [],
				};
			});
			this.entities.push({
				id: elementId,
				name: name || elementId,
				children,
				temp: true,
				tags: [],
			});
		});
		this.$root.$on('unselected-building', async (msg) => {
			this.entities.splice(
				this.entities.findIndex((x) => x.temp && x.id === msg.id),
				1
			);
		});
		this.$root.$on('focused-filter-property', async (msg) => {
			this.focusedEntityId = msg.id;
			this.focusedEntityFilterProperty = msg.filterProperty;
			this.focusedEntityFilter = msg.filter;
		});		
		this.getFiles();
	},
	mounted() {
		if (this.$store.state.scenario !== null) this.$czm('getTreeMenu');
	},
	methods: {
		...mapMutations([
			'showHideAllTags',
			'toggleTagVisibility',
			'setOsmSelect',
			'setBuildingsSelected',
			'setElementEditorEntityDataId',
		]),
		clickInput() {
			this.$refs.fileInput.click();
		},
		prepareFileUpload(event) {
			[this.file] = event.target.files;
			this.uploadFileName = this.file.name;

			const fileReader = new FileReader();
			const that = this;
			fileReader.onload = function fileReaderOnload(e) {
				crypto.subtle
					.digest('SHA-256', e.target.result)
					.then((hashBuffer) => {
						const hashArray = Array.from(new Uint8Array(hashBuffer));
						that.fileHash = hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
						that.checkFileHash(that.fileHash);
					})
					.catch((error) => {
						console.error(error);
						this.fileHash = null;
					});
			};
			fileReader.readAsArrayBuffer(this.file);
		},
		checkFileHash(fileHash) {
			// Call /files/hash to determine if file is previously uploaded
			// If 204 (No Content) there is no matching file, so upload
			// If 200, the metadata of the matching file is returned, so continue as if you uploaded
			this.axios.get(`/files/hash/${fileHash}`).then((response) => {
				if (response.status === 200) {
					// 200: match; 204: no match
					// this file has already been uploaded.
					// we need a workflow as to how the user should be notified
					// for now, close modal and pop a toast
					this.$bvModal.hide('entity-upload');
					this.file = null;
					this.uploadFileName = this.$t('file-manager.no-file-selected');
					this.$bvToast.toast(this.$t('file-manager.matching-hash-description'), {
						title: this.$t('file-manager.matching-hash-title'),
						variant: 'warning',
					});
				}
			});
		},
		uploadSelectedFile() {
			// upload file
			const formData = new FormData();
			formData.append('file', this.file);

			this.axios
				.put(`/files`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
				.then((data) => {
					data.data.user = { ...this.$store.state.user };
					data.data.fileUri = `dmp:file:${data.data.id}`;
					this.filesInView.push(data.data);
					this.readFile(data.data)
					this.file = null;
					this.uploadFileName = this.$t('file-manager.no-file-selected');
					this.$bvToast.toast(this.$t('file-manager.uploaded-description'), {
						title: this.$t('file-manager.uploaded-title'),
						variant: 'success',
					});
				})
				.catch((error) => {
					console.error(error);
				});
		},
		getFiles(searchValue = null) {
			let url = '/files/enterprise';
			const qp = new URLSearchParams();
			if (searchValue) qp.set('name', searchValue);
			url = `${url}?${qp.toString()}`;
			this.axios.get(url).then((response) => {
				this.filesInView = [];
				for (const item of response.data.filter(x=>x.name.match(/gltf|topojson/))) {
					this.filesInView.push(item);
				}
			});
		},		
		readFile(el) {
			this.$refs['entity-upload'].hide()
			this.$czm('importFile', el)
			
		},
		setProperty(id, property, value) {
			let path;
			Object.entries(flatten(this.entities)).find((x) => {
				if (x[1] === id && x[0].match(/\.id$/)) {
					path = x[0].split('.');
					path[path.length - 1] = property;
				}
				return path;
			});
			return _set(this.entities, path, value);
		},
		setEntityColor(color) {
			this.colors = color;
			this.$czm(
				'setEntityAttribute',
				this.focusedEntityId,
				'color',
				color,
				!this.focusedEntityFilterProperty
					? null
					: {
						filter: this.focusedEntityFilter,
						property: this.focusedEntityFilterProperty,
					}
			);
			// this.debounceMethod(this.postOsmStyle);
		},
		debounceMethod: _debounce((fn) => {
			fn();
		}, 1000),
		postOsmStyle() {
			this.$czm('postOsmStyle');
		},
		setEntityVisibility(data) {
			const id = data.filter ? data.parent : data.id;
			const filter = data.filter
				? {
					filter: data.filter,
					property: data.name,
				}
				: null;
			this.$czm(
				'setEntityAttribute',
				id,
				'show',
				data.checked,
				filter
			);
			/* this.$czm('setEntityVisibility', data.id, data.checked); */
		},
		async setFocusedEntity(entity) {
			if (this.focusedEntityId === entity.id) return;
			const filterProperty =
				entity.data && entity.data[0].data.filterProperty
					? entity.data[0].data.filterProperty
					: null;
			const filter =
				entity.data && entity.data[0].data.filter
					? entity.data[0].data.filter
					: null;
			this.focusedEntityFilterProperty = filterProperty;
			this.focusedEntityFilter = filter;
			let model;
			let id;

			const setColor = (input) => {
				const { data } = input;
				if (data.color && data.color.match('rgba')) {
					this.focusedName = data.name;
					const colors = data.color
						.replace(/rgba\((.*?)\)/, '$1')
						.split(',');
					const rgba = {
						r: colors[0],
						g: colors[1],
						b: colors[2],
						a: colors[3],
					};
					this.colors = { rgba };
				} /* else {
						this.colors = {
							rgba: { r: 255, g: 255, b: 255, a: 0.4 },
						};
					} */
				if (data.rawGeojson && data.rawGeojson.objects) {
					this.focusedName = data.name;

					const dataColor =
						data.rawGeojson.objects.importedGeometry
							.geometries[0].properties.collectedProperties[0]
							.color;

					const colors = dataColor.split(',');
					const rgba = {
						r: colors[0],
						g: colors[1],
						b: colors[2],
						a: colors[3] / 255,
					};
					this.colors = { rgba };
				}
			};

			const isEntity = await this.$czm(
				'getCesiumData'
			).entities.values.filter(
				(x) => x.layer && x.layer === entity.id
			);

			if (isEntity) setColor(entity.data[0]);

			this.$czm('setFocusedEntity', entity.id);

			if (entity.tags.includes('osm')) {
				if (entity.type === 'layer') {
					model = entity.children.find(
						(x) => x.type === 'osmBuildingModel'
					);
				}
				if (entity.type === 'osmBuildingModel') {
					model = entity;
				}
				if (model) this.focusedEntityId = model.id;
				this.$czm('setFocusedEntity', entity.id);

				if (model) setColor(this.$czm('getDisplayData', model.id));

				if (model && this.editMode) this.onZoom(entity.id);
			} else {
				this.focusedEntityId = entity.id;
			}
		},
		onZoom(id) {
			this.$czm('onZoom', id);
		},
		onEdit(entity) {
			this.editMode = true;
			this.focusedEntityFilterProperty = entity.name ?? null;
			this.focusedEntityFilter = entity.filter ?? null;
			/* this.$czm('setFocusedEntity', entity); */
		},
		onDelete(entity) {
			this.$czm('deleteEntity', entity);
		},
		onDblclick(id) {
			this.onZoom(id);
			this.$czm('emitEntityClicked', id);
		},
		toggleOsmSelectionMode(val) {
			this.setOsmSelect(val);
			this.$czm('toggleOsmSelectionMode', val);
			if (!val) {
				let i = this.entities.length;
				while (i--) {
					if (this.entities[i].temp === true) {
						this.entities.splice(i, 1);
					}
				}
			}
		},
		addAllSelectedOsmBuildings() {
			this.$store.commit('setBuildingsSelected', false);
			this.$czm('addAllSelectedOsmBuildings');
			this.toggleOsmSelectionMode(false);
		},
		queryFilter(result, entity) {
			if (
				entity.name.toLowerCase().includes(this.query.toLowerCase())
			) {
				result.push(entity);
				return result;
			}
			const children = entity.children.reduce(this.queryFilter, []);
			if (children.length) result.push({ ...entity, children });
			return result;
		},
		tagFilter(result, entity) {
			if (
				entity.tags.some((entityTag) =>
					this.tags.some(
						(tag) => tag.visible && tag.name === entityTag
					)
				)
			) {
				result.push(entity);
				return result;
			}
			const children = entity.children.reduce(this.tagFilter, []);
			if (children.length) result.push({ ...entity, children });
			return result;
		},
		updateFilters(name) {
			this.toggleTagVisibility(name);
		},
	},
};
</script>

<style scoped>
.element-list {
	list-style: none;
	padding: 0;
	height: 42.5vh;
}

.name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.edit-class {
	height: calc(86vh - 45px);
}

.edit-tool {
	display: flex;
	flex-direction: column;
	width: 100%;
	text-align: center;
	align-content: center;
	align-items: center;
	justify-content: center;
	margin-top: 5px;
}

.file-upload {
	border: dashed #333333;
	border-radius: 15px;
	color: #979797;
	text-align: center;
}

.hidden {
	display: inline;
}

.browse-files {
	background: transparent;
	border: none;
	color: #697781;
	text-decoration: underline;
}

.selected-file {
	color: #333333;
	font-weight: bold;
}
</style>
<style lang="scss" scoped>
header {
	span {
		line-height: 2em;
	}

	button.btn {
		height: 2rem;
		font-size: 0.4em;
	}
}
</style>
